/**
 * Public demo report. Renders the REAL v2 report components — the pre-prod
 * renderer vendored under `application-detail-v2` (scripts/vendor-report-v2.mjs)
 * — over fixtures exported from real production applications and scrubbed of
 * PII. A right-hand sidebar switches between the demo applications and the
 * Standard | Enterprise score display. Reached from the /demo flow after the
 * applicant "submits".
 *
 * URL: `/demo-report?variant=<id>&score=band|numeric`.
 */
import {
	type ActionFunctionArgs,
	type MetaFunction,
	data,
	useSearchParams,
} from 'react-router'
import { DetailPageController } from '#app/components/application-detail-v2/DetailPageController'
import { CombinedController } from '#app/components/application-detail-v2/combined/CombinedController'
import {
	DEMO_VARIANT_GROUPS,
	type DemoVariant,
	type ScoreDisplay,
	buildDemoReport,
	demoHeadlineScore,
	demoShowsScoreSwitch,
	demoVariantMeta,
	parseDemoVariant,
	parseScoreDisplay,
} from '#app/components/application-detail-v2/demo/demo-report-data'
import { RETURN_TO_PARAM } from '#app/utils/application-detail-v2/return-to'
import { cn } from '#app/utils/misc'

/**
 * No-op action. The real report dialogs each own a `useFetcher` that posts
 * intents to the current route's action; on the demo we persist nothing and
 * just acknowledge so nothing errors.
 */
export async function action({ request }: ActionFunctionArgs) {
	const form = await request.formData()
	const intent = form.get('intent')
	return data({
		ok: true as const,
		intent: typeof intent === 'string' ? intent : null,
	})
}

export const meta: MetaFunction = () => [{ title: 'Demo Report | TrueTenant' }]

const DOT_TONE = {
	green: 'bg-green-500',
	amber: 'bg-amber-400',
	red: 'bg-red-500',
	gray: 'bg-[#B8B8B8]',
} as const

// ── Demo sidebar (variant switcher + score display) ──────────────────────────

/** Desktop demo rail width — keep in sync with `lg:pr-*` on the report shell. */
const DEMO_SIDEBAR_WIDTH = 'w-72'
const DEMO_SIDEBAR_PAD = 'lg:pr-72'

function ScoreDisplaySwitch({
	value,
	onChange,
}: {
	value: ScoreDisplay
	onChange: (value: ScoreDisplay) => void
}) {
	return (
		<div className="min-w-0 w-full">
			<p className="mb-1.5 text-[10px] font-semibold tracking-wider text-[#747474] uppercase">
				Scoring
			</p>
			<div
				aria-label="Score display"
				className="flex min-w-0 w-full rounded-xl border border-[#F0F0F0] bg-white p-1"
			>
				{(
					[
						['band', 'Standard'],
						['numeric', 'Enterprise'],
					] as const
				).map(([id, label]) => {
					const active = value === id
					return (
						<button
							key={id}
							type="button"
							aria-pressed={active}
							onClick={() => onChange(id)}
							className={cn(
								'min-w-0 flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-all',
								active
									? 'bg-[#32302F] text-white'
									: 'text-[#747474] hover:text-[#32302F]',
							)}
						>
							{label}
						</button>
					)
				})}
			</div>
		</div>
	)
}

function VariantButton({
	variant,
	active,
	compact,
	onSelect,
}: {
	variant: DemoVariant
	active: boolean
	compact?: boolean
	onSelect: (variant: DemoVariant) => void
}) {
	const meta = demoVariantMeta(variant)
	const score = demoHeadlineScore(variant)
	if (compact) {
		return (
			<button
				type="button"
				onClick={() => onSelect(variant)}
				className={cn(
					'shrink-0 rounded-xl border px-3 py-2 text-xs font-medium whitespace-nowrap transition-all',
					active
						? 'border-[#32302F] bg-[#32302F] text-white'
						: 'border-[#F0F0F0] bg-white text-[#32302F]',
				)}
			>
				{meta.label}
			</button>
		)
	}
	return (
		<button
			type="button"
			onClick={() => onSelect(variant)}
			aria-current={active ? 'page' : undefined}
			className={cn(
				// Inactive used the same fill as the rail (`#fbfdfd`), so only the
				// dark selected card showed a hard right edge — looked like it
				// alone was overflowing. White fill keeps every card's inset equal.
				'box-border w-full max-w-full min-w-0 rounded-[20px] border px-3.5 py-3.5 text-left transition-all',
				active
					? 'border-[#32302F] bg-[#32302F]'
					: 'border-[#F0F0F0] bg-white hover:border-[#AAAAAA]',
			)}
		>
			<div className="mb-1 flex min-w-0 items-center gap-2">
				<span
					className={cn(
						'size-2 shrink-0 rounded-full',
						active ? 'bg-white opacity-60' : DOT_TONE[meta.tone],
					)}
				/>
				<span
					className={cn(
						'min-w-0 truncate text-[11px] font-semibold tracking-wide uppercase',
						active ? 'text-gray-300' : 'text-[#747474]',
					)}
				>
					{meta.eyebrow}
					{score != null ? ` · ${score}/100` : ''}
				</span>
			</div>
			<p
				className={cn(
					'truncate text-base leading-tight font-semibold',
					active ? 'text-white' : 'text-[#32302F]',
				)}
			>
				{meta.label}
			</p>
		</button>
	)
}

function DemoSidebar({
	current,
	scoreDisplay,
	onChange,
	onScoreDisplayChange,
}: {
	current: DemoVariant
	scoreDisplay: ScoreDisplay
	onChange: (variant: DemoVariant) => void
	onScoreDisplayChange: (value: ScoreDisplay) => void
}) {
	return (
		<>
			{/* Outer shell owns the rail width; inner scroller owns equal padding
			    so cards never sit flush against the viewport edge. */}
			<aside
				className={cn(
					'fixed top-0 right-0 bottom-0 z-40 hidden flex-col overflow-hidden border-l border-[#EDEDEB] bg-[#fbfdfd] lg:flex',
					DEMO_SIDEBAR_WIDTH,
				)}
			>
				{/* Extra right pad so a Windows scrollbar can't eat the card's
				    rounded corner — same visual inset as the left. */}
				<div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-x-hidden overflow-y-auto py-4 pr-5 pl-4">
					<p className="text-xs font-semibold tracking-wide text-[#747474] uppercase">
						Demo Reports
					</p>
					{demoShowsScoreSwitch(current) ? (
						<ScoreDisplaySwitch
							value={scoreDisplay}
							onChange={onScoreDisplayChange}
						/>
					) : null}
					<div className="flex min-w-0 flex-col gap-2">
						{DEMO_VARIANT_GROUPS.flatMap((group) => group.variants).map(
							(meta) => (
								<VariantButton
									key={meta.id}
									variant={meta.id}
									active={current === meta.id}
									onSelect={onChange}
								/>
							),
						)}
					</div>
				</div>
			</aside>
			<div className="fixed right-0 bottom-0 left-0 z-40 flex flex-col gap-2 border-t border-[#EDEDEB] bg-[#fbfdfd] px-4 py-2.5 lg:hidden">
				<div className="flex gap-2 overflow-x-auto">
					{DEMO_VARIANT_GROUPS.flatMap((group) => group.variants).map(
						(meta) => (
							<VariantButton
								key={meta.id}
								variant={meta.id}
								active={current === meta.id}
								compact
								onSelect={onChange}
							/>
						),
					)}
				</div>
				{demoShowsScoreSwitch(current) ? (
					<ScoreDisplaySwitch
						value={scoreDisplay}
						onChange={onScoreDisplayChange}
					/>
				) : null}
			</div>
		</>
	)
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function DemoReport() {
	// URL is the state: the vendored header's Back arrow reads `returnTo` from
	// the same search params, and the /demo flow deep-links `?variant=`.
	const [searchParams, setSearchParams] = useSearchParams()
	const variant = parseDemoVariant(searchParams.get('variant'))
	const scoreDisplay = parseScoreDisplay(searchParams.get('score'))
	const update = (next: { variant?: DemoVariant; score?: ScoreDisplay }) =>
		setSearchParams(
			{
				variant: next.variant ?? variant,
				score: next.score ?? scoreDisplay,
				[RETURN_TO_PARAM]: '/demo',
			},
			{ preventScrollReset: true },
		)
	const report = buildDemoReport(variant, scoreDisplay)

	return (
		<div className="min-h-screen bg-[#fbfdfd]">
			<DemoSidebar
				current={variant}
				scoreDisplay={scoreDisplay}
				onChange={(next) => update({ variant: next })}
				onScoreDisplayChange={(next) => update({ score: next })}
			/>

			{/* Real report component, padded clear of the fixed sidebar. Keyed so
			    switching applications remounts dialog/switcher state. */}
			<div className={cn('pb-32 lg:pb-0', DEMO_SIDEBAR_PAD)}>
				{report.kind === 'combined' ? (
					<CombinedController key={variant} data={report.data} />
				) : (
					<DetailPageController key={variant} data={report.data} />
				)}
			</div>
		</div>
	)
}
